<template>
  <parent>
    <validation-observer
      ref="outputPresetObserver"
      v-slot="{ invalid }"
      tag="form"
    >
      <b-container>
        <b-row class="justify-content-center">
          <b-col
            cols="12"
            md="10"
          >
            <paper
              title="Preset Settings"
              body-class="mt-0"
            >
              <div class="px-2">
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Preset name"
                      rules="required"
                      class="w-100"
                    >
                      <input-field
                        name="preset_name"
                        label="Preset Name*"
                        label-position="top"
                        :info="generalInfo.presetName"
                        info-position="label"
                        :error="errors.length ? errors[0] : null"
                        @openInfo="openInfo"
                      >
                        <b-form-input
                          v-if="isEditable"
                          id="preset_name"
                          v-model="outputPreset.name"
                          placeholder="Enter preset name here..."
                          :state="errors.length > 0 ? false : null"
                        />
                        <div v-else>
                          {{ outputPreset.name }}
                        </div>
                      </input-field>
                    </validation-provider>
                  </b-col>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Description"
                      class="w-100"
                    >
                      <input-field
                        label="Description"
                        name="preset_description"
                        label-position="top"
                        :error="errors[0]"
                        label-weight="medium"
                      >
                        <b-form-input
                          v-if="isEditable"
                          id="preset_name"
                          v-model="outputPreset.description"
                          placeholder="Enter description here..."
                          :state="errors.length > 0 ? false : null"
                        />
                        <div v-else>
                          {{ outputPreset.description }}
                        </div>
                      </input-field>
                    </validation-provider>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Category"
                      class="w-100"
                    >
                      <input-field
                        name="preset_category"
                        label="Category"
                        label-position="top"
                        :error="errors[0]"
                      >
                        <b-form-input
                          v-if="isEditable"
                          id="preset_category"
                          v-model="outputPreset.category"
                          placeholder="Enter category here..."
                          :state="errors.length > 0 ? false : null"
                        />
                        <div v-else>
                          {{ outputPreset.category }}
                        </div>
                      </input-field>
                    </validation-provider>
                  </b-col>
                  <b-col
                    cols="12"
                    sm="6"
                  >
                    <validation-provider
                      #default="{ errors }"
                      name="Container"
                      class="w-100"
                    >
                      <input-field
                        name="preset_container"
                        label="Container"
                        label-position="top"
                        :error="errors[0]"
                      >
                        <custom-select
                          v-model="outputPreset.container.type"
                          :options="
                            getOptions(outputPreset.container.containerOptions)
                          "
                        />
                      </input-field>
                    </validation-provider>
                  </b-col>
                </b-row>
              </div>
            </paper>
          </b-col>
        </b-row>
        <b-row class="justify-content-center">
          <b-col
            cols="12"
            md="10"
          >
            <paper-tabbed title="Encoding Settings">
              <template
                v-if="isEditable"
                v-slot:header-right
              >
                <b-button
                  v-if="!videoDescription"
                  variant="outline-primary"
                  class="btn-paper-header"
                  @click="addVideoDescription"
                >
                  Add Video
                </b-button>
                <b-button
                  v-if="!audioDescription"
                  variant="outline-primary"
                  class="btn-paper-header ml-1"
                  @click="addAudioDescription"
                >
                  Add Audio
                </b-button>
              </template>
              <paper-tab
                v-if="!audioDescription && !videoDescription"
                title="None"
              >
                <div class="p-2">
                  <b-row>
                    <b-col>
                      Start by adding Video & Audio Encoding Settings
                    </b-col>
                  </b-row>
                </div>
              </paper-tab>
              <paper-tab
                v-if="videoDescription"
                title="Video"
                :active="videoTabActive"
              >
                <video-settings
                  ref="videoSettingsRef"
                  :is-editable="isEditable"
                  :edit="edit || duplicating"
                  @openInfo="openInfo"
                  @remove="removeVideoDescription"
                />
              </paper-tab>
              <paper-tab
                v-if="audioDescription"
                title="Audio"
                :active="audioTabActive"
              >
                <div class="px-2 pt-2">
                  <audio-settings
                    ref="audioSettingsRef"
                    :is-editable="isEditable"
                    :output-preset="outputPreset"
                    :edit="edit || duplicating"
                    @openInfo="openInfo"
                    @remove="removeAudioDescription"
                  />
                </div>
              </paper-tab>
            </paper-tabbed>
          </b-col>
        </b-row>
        <div
          v-if="isEditable"
          class="d-flex justify-content-end py-2 border-default border-t mt-1"
        >
          <b-button
            variant="bg-white"
            :to="{
              name: $route.params.from || 'output-presets',
              params: {
                backFromOutputPresets: true,
                activeTab: $route.params.activeTab,
              },
              query: $route.query,
            }"
          >
            Cancel
          </b-button>
          <button-loading
            variant="bg-primary"
            class="ml-1"
            :disabled="invalid || submitLoading"
            :spinning="spinning"
            @click="submit"
          >
            Save Changes
          </button-loading>
        </div>
      </b-container>
      <info
        v-if="infoData"
        :info="infoData"
        @close="infoData = null"
      />
    </validation-observer>
  </parent>
</template>

<script>
import { ValidationProvider, ValidationObserver } from 'vee-validate'

import { required } from '@validations'

import Paper from '@/components/paper/Paper.vue'
import PaperTabbed from '@/components/paper/PaperTabbed.vue'
import PaperTab from '@/components/paper/PaperTab.vue'
import InputField from '@/components/form/InputField.vue'
import helpers from '@/mixins/helpers'
import generalInfo from './generalInfo'
import VideoSettings from './settings/VideoSettings.vue'
import AudioSettings from './settings/AudioSettings.vue'

export default {
  name: 'OutputPresetForm',
  components: {
    ValidationProvider,
    ValidationObserver,
    Paper,
    InputField,
    PaperTabbed,
    PaperTab,
    VideoSettings,
    AudioSettings,
  },
  mixins: [helpers],
  props: {
    edit: {
      type: Boolean,
      default: false,
    },
    submitLoading: {
      type: Boolean,
      default: false,
    },
    duplicating: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      generalInfo,
      required,
      infoData: null,
      activeTab: 0,
      encodingActiveTab: 1,
      videoDescription: true,
      audioDescription: false,
      videoDescriptionModel: null,
      audioDescriptionModel: null,
      outputPreset: null,
    }
  },
  provide() {
    return { parentValidator: this.$validator }
  },
  computed: {
    videoTabActive() {
      return this.activeTab === 0
    },
    audioTabActive() {
      return this.activeTab === 1
    },
    buttonEnabled() {
      return false
    },
    isEditable() {
      if (!this.edit) {
        return true
      }
      return (
        this.outputPreset.presetType
        !== this.outputPreset.presetTypeOptions[0].id
      )
    },
    editingOutputPreset() {
      return this.$store.state.outputPresets.editingOutputPreset
    },
    creatingOutputPreset() {
      return this.$store.state.outputPresets.creatingOutputPreset
    },
    spinning() {
      return this.edit ? this.editingOutputPreset : this.creatingOutputPreset
    },
  },
  created() {
    this.init()
  },
  methods: {
    openInfo(i) {
      this.infoData = i
    },
    init() {
      if (this.edit) {
        this.outputPreset = this.$store.state.outputPresets.outputPreset
        this.videoDescription = !!this.outputPreset.videoDescription
        this.audioDescription = !!this.outputPreset.audioDescription
      } else {
        this.outputPreset = this.duplicating
          ? this.$store.state.outputPresets.outputPreset
          : this.$store.state.outputPresets.emptyOutputPreset
        this.videoDescription = !!this.outputPreset.videoDescription
        this.audioDescription = !!this.outputPreset.audioDescription
      }
      if (this.duplicating) this.outputPreset.name = ''
    },
    isAnyEncoding() {
      return !!this.videoDescription || !!this.audioDescription
    },
    async submit() {
      if (!this.isAnyEncoding()) {
        this.errorToast('Preset must have at least one video or audio')
        return
      }
      if (await this.$refs.outputPresetObserver.validate()) {
        const form = {
          ...this.outputPreset,
          audioDescription: this.audioDescription
            ? this.$refs.audioSettingsRef.audioDescription
            : null,
          videoDescription: this.videoDescription
            ? this.$refs.videoSettingsRef.videoDescription
            : null,
        }
        this.$emit('submit', form)
      }
    },
    addVideoDescription() {
      this.videoDescription = true
      this.activeTab = 0
    },
    addAudioDescription() {
      this.audioDescription = true
      this.activeTab = 1
    },
    removeVideoDescription() {
      this.videoDescription = false
      // this.$store.dispatch('outputPresets/removeVideoDescription')
    },
    removeAudioDescription() {
      this.audioDescription = false
      // this.$store.dispatch('outputPresets/removeAudioDescription')
    },
  },
}
</script>
